import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { OAuthService, OAuthStorage, TokenResponse } from "angular-oauth2-oidc";
import { BehaviorSubject, exhaustMap, tap, shareReplay, firstValueFrom, take, Observable } from "rxjs";

/**
 * Why the token endpoint refused a refresh, derived from OpenIddict's error_description.
 * - redeemed: the token had already been rotated and the reuse leeway has passed. The client never
 *   received the rotated token (lost response: laptop sleep, API restart during a deploy, network drop),
 *   so the session is stranded. This is the case the reuse leeway setting governs.
 * - no-longer-valid: the token entry expired or was revoked, typically a user away for 30+ days.
 * - invalid: the token entry cannot be found or read.
 * - other: any other rejection, or a failure that was not a token endpoint response.
 */
export type RefreshRejectionReason = 'redeemed' | 'no-longer-valid' | 'invalid' | 'other';

/** Custom properties attached to the "Refresh auth token rejected" / "Refresh auth token error" events. */
export interface RefreshFailureProperties {
  status: string;
  error: string;
  errorDescription: string;
  reason: RefreshRejectionReason;
  /** Minutes since the current token pair was stored, i.e. since the last successful login or refresh. Empty when unknown. */
  tokenAgeMinutes: string;
}

@Injectable({ providedIn: 'root' })
export class SingletonTokenRefreshService {
  private applicationInsights = inject(ApplicationInsights);
  private oauthService = inject(OAuthService);
  private oauthStorage = inject(OAuthStorage);
  private refreshEvent = new BehaviorSubject<void>(undefined);
  private refresh$ = this.refreshEvent.pipe(
    exhaustMap(() => this.oauthService.refreshToken()),
    tap({
      error: ex => this.logRefreshError(ex)
    }),
    shareReplay()
  );

  public refreshToken() : Observable<TokenResponse> {
    this.refreshEvent.next();
    return this.refresh$.pipe(take(1));
  }

  private logRefreshError(ex: any) {
    const properties = this.describeRefreshFailure(ex);

    //Detect that the refresh was rejected
    if(this.refreshExplicitlyRejected(ex)) {
      // Properties are logged too so the reason is visible in the browser console, where Application
      // Insights is disabled (local dev) or when a user sends us their console output.
      console.warn("Refresh auth token was rejected", properties, ex);
      this.applicationInsights.trackEvent({ name: 'Refresh auth token rejected', properties });

      this.oauthService.logOut();
      return;
    }

    console.warn("Received an error attempting to refresh auth token", properties, ex);
    this.applicationInsights.trackEvent({ name: 'Refresh auth token error', properties });

    var error: Error = ex instanceof Error ? ex : new Error("Refresh token error");
    this.applicationInsights.trackException({ exception: error, properties });
  }

  /**
   * True when the token endpoint told us the refresh token is no good, as opposed to a transient
   * failure. OpenIddict answers an expired, revoked or unknown refresh token (and a deleted user)
   * with 400 invalid_grant via the API's InvalidGrant helper; retrying the same token can never
   * succeed, so it counts as a rejection alongside 403. Anything else (network, 5xx, other 400s)
   * is left alone so a blip does not log the user out.
   */
  refreshExplicitlyRejected(ex: any) : boolean {
    if(!(ex instanceof HttpErrorResponse)) {
      return false;
    }

    return ex.status == 403 || (ex.status == 400 && ex.error?.error === 'invalid_grant');
  }

  /**
   * Records what the token endpoint said and how old the stored token was, so telemetry can tell a
   * user who has simply been away for 30+ days from a session stranded by a lost rotation response.
   * Every value is a string because Application Insights custom properties are strings.
   */
  describeRefreshFailure(ex: any): RefreshFailureProperties {
    const response = ex instanceof HttpErrorResponse ? ex : null;
    const error = typeof response?.error?.error === 'string' ? response.error.error : '';
    const errorDescription = typeof response?.error?.error_description === 'string' ? response.error.error_description : '';

    return {
      status: response ? String(response.status) : '',
      error,
      errorDescription,
      reason: classifyRefreshRejection(errorDescription),
      tokenAgeMinutes: this.getTokenAgeMinutes(),
    };
  }

  /** angular-oauth2-oidc stores the epoch milliseconds at which the current token pair was saved. */
  private getTokenAgeMinutes(): string {
    try {
      const storedAt = Number(this.oauthStorage.getItem('access_token_stored_at'));
      if (!Number.isFinite(storedAt) || storedAt <= 0) return '';
      return String(Math.max(0, Math.round((Date.now() - storedAt) / 60000)));
    } catch {
      return '';
    }
  }
}

/** Maps OpenIddict's refresh token error_description to a stable reason for alert dimensions. */
export function classifyRefreshRejection(errorDescription: string | null | undefined): RefreshRejectionReason {
  const text = (errorDescription ?? '').toLowerCase();
  if (text.includes('already been redeemed')) return 'redeemed';
  if (text.includes('no longer valid')) return 'no-longer-valid';
  if (text.includes('refresh token is invalid')) return 'invalid';
  return 'other';
}
